import { resolveTimeZone, parseStationDateTime } from './flight-documentation-window';

/**
 * Asociación PNR ↔ Flight Status (CI-22450).
 *
 * El `segmentCode` del PNR embebe la hora programada (`..._2210`), mientras que
 * Flight Status puede devolver el código con otra hora cuando hay afectación
 * (`..._2155`). Por eso la coincidencia exacta de `segmentCode` no puede ser el
 * único criterio, pero tampoco basta el número de vuelo: dos segmentos del mismo
 * vuelo el mismo día, un codeshare o rutas distintas producirían un cruce.
 *
 * Criterio determinista, sin tolerancias arbitrarias de minutos:
 *   1. Coincidencia exacta de `segmentCode`.
 *   2. Si no existe, se filtran candidatos por origen + destino + carrier
 *      compatible + número de vuelo normalizado + fecha operacional.
 *   3. Solo se acepta el fallback cuando hay EXACTAMENTE un candidato.
 *   4. Cero candidatos o ambigüedad → `null` (no se contamina el segmento).
 */

/** Entrada de `flightStatusCollection`. */
export interface FlightStatusEntryLike {
  segmentCode?: string;
  departureAirport?: string;
  arrivalAirport?: string;
  marketingCarrier?: string;
  operatingCarrier?: string;
  marketingFlightCode?: string;
  operatingFlightCode?: string;
  estimatedDepartureTime?: string;
  originalDepartureTime?: string;
  estimatedArrivalTime?: string;
  arrivalDateTime?: string;
  departureDifferenceInMinutes?: number;
  status?: string;
}

/** Segmento del PNR contra el que se compara. */
export interface MatchableSegmentLike {
  segmentCode?: string;
  departureAirport?: string;
  arrivalAirport?: string;
  marketingCarrier?: string;
  operatingCarrier?: string;
  marketingFlightCode?: string;
  operatingFlightCode?: string;
  departureDateTime?: string;
  estimatedDepartureTime?: string;
  departureAirportTimeZoneId?: string;
}

/**
 * Número de vuelo normalizado: quita un prefijo de carrier alfabético y los
 * ceros a la izquierda. "264", "0264", "AM264" y "AM0264" → "264".
 * Devuelve `null` cuando no hay dígitos (no se asume `parseInt` directo, que
 * daría `NaN` con "AM264").
 */
export function normalizeFlightNumber(value: string | undefined | null): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  const digits = `${value}`.trim().match(/(\d+)\s*$/);
  if (!digits) {
    return null;
  }
  const normalized = digits[1].replace(/^0+/, '');
  return normalized === '' ? '0' : normalized;
}

/** Código de aeropuerto normalizado para comparación. */
function normalizeCode(value: string | undefined | null): string {
  return (value ?? '').trim().toUpperCase();
}

/**
 * Carrier compatible: se acepta si el carrier de Flight Status coincide con el
 * marketing o el operating carrier del segmento (codeshare). Nunca se omite la
 * validación de carrier.
 */
function hasCompatibleCarrier(
  segment: MatchableSegmentLike,
  entry: FlightStatusEntryLike
): boolean {
  const segmentCarriers = [segment.marketingCarrier, segment.operatingCarrier]
    .map(normalizeCode)
    .filter((c) => c !== '');
  const entryCarriers = [entry.marketingCarrier, entry.operatingCarrier]
    .map(normalizeCode)
    .filter((c) => c !== '');

  // Sin dato de carrier en alguno de los lados no se puede validar: se exige
  // que ambos lo tengan para no cruzar segmentos por número de vuelo.
  if (!segmentCarriers.length || !entryCarriers.length) {
    return false;
  }

  return entryCarriers.some((entryCarrier) => segmentCarriers.includes(entryCarrier));
}

/**
 * Número de vuelo compatible entre segmento y entrada, considerando marketing y
 * operating (codeshare).
 */
function hasCompatibleFlightNumber(
  segment: MatchableSegmentLike,
  entry: FlightStatusEntryLike
): boolean {
  const segmentNumbers = [segment.marketingFlightCode, segment.operatingFlightCode]
    .map(normalizeFlightNumber)
    .filter((n): n is string => n !== null);
  const entryNumbers = [entry.marketingFlightCode, entry.operatingFlightCode]
    .map(normalizeFlightNumber)
    .filter((n): n is string => n !== null);

  if (!segmentNumbers.length || !entryNumbers.length) {
    return false;
  }

  return entryNumbers.some((entryNumber) => segmentNumbers.includes(entryNumber));
}

/** Fecha operacional (YYYY-MM-DD) de un datetime interpretado en la estación. */
function operationalDate(
  value: string | undefined | null,
  timeZoneId: string | undefined | null
): string | null {
  const parsed = parseStationDateTime(value, timeZoneId);
  if (!parsed) {
    return null;
  }
  return parsed.tz(resolveTimeZone(timeZoneId)).format('YYYY-MM-DD');
}

/**
 * Fecha operacional (YYYY-MM-DD) embebida en el `segmentCode`
 * (`ORIG_DEST_CARRIER_FLIGHT_YYYY-MM-DD_HHMM`). Es la misma fecha en el código
 * del PNR y en el de Flight Status aunque difieran en la hora (`..._2210` vs
 * `..._2155`), por lo que sirve de respaldo cuando el segmento no trae STD/ETD.
 * No aporta la hora, así que nunca relaja los demás criterios.
 */
function segmentCodeOperationalDate(segmentCode: string | undefined | null): string | null {
  const match = `${segmentCode ?? ''}`.match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * Fechas operacionales de un lado: las de sus datetimes (autoritativas) y, SOLO
 * si no hay ninguna válida, la embebida en su `segmentCode` como respaldo. El
 * `segmentCode` conserva la fecha programada aunque el datetime real cambie de
 * día, por eso nunca sustituye a un datetime presente (evita falsos positivos
 * cuando la afectación mueve la fecha operacional).
 */
function operationalDatesOf(
  dateTimeValues: (string | undefined | null)[],
  segmentCode: string | undefined | null,
  timeZoneId: string | undefined | null
): string[] {
  const fromDateTimes = dateTimeValues
    .map((value) => operationalDate(value, timeZoneId))
    .filter((d): d is string => d !== null);
  if (fromDateTimes.length) {
    return fromDateTimes;
  }
  const fromCode = segmentCodeOperationalDate(segmentCode);
  return fromCode ? [fromCode] : [];
}

/**
 * Fecha operacional compatible. Se comparan las fechas del segmento (STD/ETD, o
 * su `segmentCode` si no hay STD/ETD) contra las de la entrada (original/estimada,
 * o su `segmentCode` si no las hay), interpretadas en la zona de la estación.
 * Basta que alguna combinación coincida, lo que cubre las afectaciones que cruzan
 * la medianoche y los segmentos sin STD/ETD, sin inventar tolerancias en minutos.
 */
function hasCompatibleOperationalDate(
  segment: MatchableSegmentLike,
  entry: FlightStatusEntryLike
): boolean {
  const timeZoneId = segment.departureAirportTimeZoneId;

  const segmentDates = operationalDatesOf(
    [segment.departureDateTime, segment.estimatedDepartureTime],
    segment.segmentCode,
    timeZoneId
  );
  const entryDates = operationalDatesOf(
    [entry.originalDepartureTime, entry.estimatedDepartureTime],
    entry.segmentCode,
    timeZoneId
  );

  // Sin fechas comparables no se afirma compatibilidad.
  if (!segmentDates.length || !entryDates.length) {
    return false;
  }

  return entryDates.some((entryDate) => segmentDates.includes(entryDate));
}

/** Resultado de la asociación, con el motivo para trazabilidad y pruebas. */
export type FlightStatusMatchReason =
  | 'EXACT_SEGMENT_CODE'
  | 'UNIQUE_CANDIDATE'
  | 'NO_CANDIDATE'
  | 'AMBIGUOUS';

export interface FlightStatusMatchResult {
  entry: FlightStatusEntryLike | null;
  reason: FlightStatusMatchReason;
  candidateCount: number;
}

/**
 * Resuelve la entrada de Flight Status que corresponde al segmento, o `null`
 * cuando no hay coincidencia inequívoca. Nunca elige "el primero" de forma
 * arbitraria ni depende de `flightStatusCollection[0]`.
 */
export function matchFlightStatusForSegment(
  segment: MatchableSegmentLike | null | undefined,
  entries: FlightStatusEntryLike[] | null | undefined
): FlightStatusMatchResult {
  const collection = (entries ?? []).filter((entry) => !!entry);
  if (!segment || !collection.length) {
    return { entry: null, reason: 'NO_CANDIDATE', candidateCount: 0 };
  }

  const segmentCode = normalizeCode(segment.segmentCode);
  if (segmentCode) {
    const exact = collection.filter((entry) => normalizeCode(entry.segmentCode) === segmentCode);
    if (exact.length === 1) {
      return { entry: exact[0], reason: 'EXACT_SEGMENT_CODE', candidateCount: 1 };
    }
    if (exact.length > 1) {
      return { entry: null, reason: 'AMBIGUOUS', candidateCount: exact.length };
    }
  }

  const candidates = collection.filter(
    (entry) =>
      normalizeCode(entry.departureAirport) === normalizeCode(segment.departureAirport) &&
      normalizeCode(entry.arrivalAirport) === normalizeCode(segment.arrivalAirport) &&
      hasCompatibleCarrier(segment, entry) &&
      hasCompatibleFlightNumber(segment, entry) &&
      hasCompatibleOperationalDate(segment, entry)
  );

  if (candidates.length === 1) {
    return { entry: candidates[0], reason: 'UNIQUE_CANDIDATE', candidateCount: 1 };
  }

  return {
    entry: null,
    reason: candidates.length === 0 ? 'NO_CANDIDATE' : 'AMBIGUOUS',
    candidateCount: candidates.length,
  };
}
